import React from 'react';
import { Grid, Trash2, PlusCircle, Star } from 'lucide-react';

const GridQuestionEditor = ({
  question,
  updateQuestion,
  theme,
  isActive
}) => {
  const addRow = () => {
    const newRows = [...(question.settings?.gridRows || []), ''];
    updateQuestion(question._id, {
      settings: { ...question.settings, gridRows: newRows }
    });
  };

  const addColumn = () => {
    const newCols = [...(question.settings?.gridColumns || []), ''];
    updateQuestion(question._id, {
      settings: { ...question.settings, gridColumns: newCols }
    });
  };

  const updateRow = (index, value) => {
    const newRows = [...(question.settings?.gridRows || [])];
    newRows[index] = value;
    updateQuestion(question._id, {
      settings: { ...question.settings, gridRows: newRows }
    });
  };

  const updateColumn = (index, value) => {
    const newCols = [...(question.settings?.gridColumns || [])];
    newCols[index] = value;
    updateQuestion(question._id, {
      settings: { ...question.settings, gridColumns: newCols }
    });
  };

  const removeRow = (index) => {
    const newRows = (question.settings?.gridRows || []).filter((_, i) => i !== index);
    updateQuestion(question._id, {
      settings: { ...question.settings, gridRows: newRows }
    });
  };

  const removeColumn = (index) => {
    const newCols = (question.settings?.gridColumns || []).filter((_, i) => i !== index);
    updateQuestion(question._id, {
      settings: { ...question.settings, gridColumns: newCols }
    });
  };

  return (
    <div className="space-y-6">
      {/* Grid Type Selection */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => updateQuestion(question._id, {
            settings: { ...question.settings, gridType: 'rating' }
          })}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors
            ${question.settings?.gridType === 'rating' ? 'bg-blue-50 text-blue-600' : 'text-gray-600'}`}
        >
          <Star className="h-4 w-4" />
          <span>Rating Grid</span>
        </button>
        <button
          onClick={() => updateQuestion(question._id, {
            settings: { ...question.settings, gridType: 'numeric' }
          })}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors
            ${question.settings?.gridType === 'numeric' ? 'bg-blue-50 text-blue-600' : 'text-gray-600'}`}
        >
          <Grid className="h-4 w-4" />
          <span>Numeric Grid</span>
        </button>
      </div>

      {/* Rating Configuration */}
      {question.settings?.gridType === 'rating' && (
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700">Maximum Rating</label>
          <select
            value={question.settings?.maxRating || 5}
            onChange={(e) => updateQuestion(question._id, {
              settings: { ...question.settings, maxRating: parseInt(e.target.value) }
            })}
            className="mt-1 block w-32 rounded-md border-gray-300"
          >
            {[3, 4, 5, 10].map(num => (
              <option key={num} value={num}>{num} Stars</option>
            ))}
          </select>
        </div>
      )}

      {/* Grid Layout */}
      <div className="grid grid-cols-2 gap-6">
        {/* Rows Section */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-gray-700">Rows</label>
            <button
              onClick={addRow}
              className="flex items-center gap-1 text-sm"
              style={{ color: theme }}
            >
              <PlusCircle className="h-4 w-4" />
              Add Row
            </button>
          </div>
          <div className="space-y-2">
            {(question.settings?.gridRows || []).map((row, index) => (
              <div key={index} className="flex items-center gap-2">
                <input
                  type="text"
                  value={row}
                  onChange={(e) => updateRow(index, e.target.value)}
                  placeholder={`Row ${index + 1}`}
                  className="flex-1 px-3 py-2 border rounded-lg text-sm"
                />
                <button
                  onClick={() => removeRow(index)}
                  className="p-1 text-gray-400 hover:text-red-500"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Columns Section */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-gray-700">Columns</label>
            <button
              onClick={addColumn}
              className="flex items-center gap-1 text-sm"
              style={{ color: theme }}
            >
              <PlusCircle className="h-4 w-4" />
              Add Column
            </button>
          </div>
          <div className="space-y-2">
            {(question.settings?.gridColumns || []).map((col, index) => (
              <div key={index} className="flex items-center gap-2">
                <input
                  type="text"
                  value={col}
                  onChange={(e) => updateColumn(index, e.target.value)}
                  placeholder={`Column ${index + 1}`}
                  className="flex-1 px-3 py-2 border rounded-lg text-sm"
                />
                <button
                  onClick={() => removeColumn(index)}
                  className="p-1 text-gray-400 hover:text-red-500"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Preview */}
      {question.settings?.gridRows?.length > 0 && question.settings?.gridColumns?.length > 0 && (
        <div className="mt-4 border rounded-lg overflow-x-auto">
          <table className="w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="px-4 py-2 bg-gray-50"></th>
                {question.settings.gridColumns.map((col, i) => (
                  <th key={i} className="px-4 py-2 bg-gray-50 text-sm text-gray-600">
                    {col || `Column ${i + 1}`}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {question.settings.gridRows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  <td className="px-4 py-2 text-sm text-gray-600">
                    {row || `Row ${rowIndex + 1}`}
                  </td>
                  {question.settings.gridColumns.map((_, colIndex) => (
                    <td key={colIndex} className="px-4 py-2 text-center">
                      {question.settings?.gridType === 'rating' ? (
                        <div className="flex items-center justify-center gap-1">
                          {Array.from({ length: question.settings?.maxRating || 5 }).map((_, i) => (
                            <Star 
                              key={i}
                              className="h-4 w-4"
                              style={{ color: theme }}
                            />
                          ))}
                        </div>
                      ) : (
                        <input
                          type="number"
                          className="w-16 px-2 py-1 text-sm border rounded"
                          placeholder="0"
                          disabled
                        />
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default GridQuestionEditor;